/* eslint-disable no-console */
import { jsPDF } from 'jspdf';

import {
  Slide,
  SlideElementTextType,
} from '../../components/SlideComponents/slide.types';
import {
  isSlideElementIframe,
  isSlideElementImage,
  isSlideElementShape,
  isSlideElementSvg,
  isSlideElementText,
} from '../../components/SlideComponents/slideUtil';
import { addBackground } from './addBackground';
import { addIframe } from './addIframe';
import { addImage } from './addImage';
import { addPollOptions } from './addPollOptions';
import { addShape } from './addShape';
import { addSvg } from './addSvg';
import {
  nunitoBase64,
  nunitoBoldBase64,
  nunitoBoldItalicBase64,
  nunitoItalicBase64,
} from './nunito.font';
import renderTextElement from './renderTextElement';
import { addLogo, getProp, parseJSON, setRotation } from './utils';

const setElementRotation = (
  pdf: jsPDF,
  degrees: unknown,
  pivotX: unknown,
  pivotY: unknown,
) => {
  if (
    typeof degrees !== 'number' ||
    typeof pivotX !== 'number' ||
    typeof pivotY !== 'number'
  )
    return;
  if (degrees % 360 === 0) return;
  setRotation(pdf, degrees, pivotX, pivotY);
};

const exportSlide = async (pdf: jsPDF, slide: Slide) => {
  pdf.addPage([slide.width, slide.height], 'landscape');
  await addBackground(pdf, slide);
  for await (const element of slide.elements) {
    pdf.setGState(pdf.GState({ opacity: element.opacity }));
    pdf.saveGraphicsState();
    setElementRotation(
      pdf,
      element.angle,
      element.x + element.width / 2,
      element.y + element.height / 2,
    );
    if (isSlideElementText(element)) {
      const elementContent =
        parseJSON(getProp(element as SlideElementTextType, 'textValue')) ?? [];
      if (Array.isArray(elementContent)) {
        await renderTextElement({
          pdf,
          textElements: elementContent,
          x: element.x,
          y: element.y,
          width: element.width,
          scale: element.scale,
        });
      }
    } else if (isSlideElementShape(element)) {
      await addShape(pdf, element);
    } else if (isSlideElementSvg(element)) {
      await addSvg(pdf, element);
    } else if (isSlideElementImage(element)) {
      await addImage(pdf, element);
    } else if (isSlideElementIframe(element)) {
      await addIframe(pdf, element);
    } else {
      console.debug(`Unknown element type - "${element.type}"`);
    }
    pdf.restoreGraphicsState();
  }
  if (slide.type === 'poll') {
    await addPollOptions(pdf, slide);
  }
  await addLogo(pdf, slide.width);
};

export const exportPdf = async (
  slides: Slide[],
  fileName: string,
  onSuccess: () => void,
) => {
  const pdf = new jsPDF({
    unit: 'pt',
    compress: true,
  });
  pdf.addFileToVFS('nunito.ttf', nunitoBase64);
  pdf.addFileToVFS('nunitoBold.ttf', nunitoBoldBase64);
  pdf.addFileToVFS('nunitoBoldItalic.ttf', nunitoBoldItalicBase64);
  pdf.addFileToVFS('nunitoItalic.ttf', nunitoItalicBase64);
  pdf.addFont('nunito.ttf', 'Nunito', 'Normal');
  pdf.addFont('nunitoBold.ttf', 'Nunito', 'Bold');
  pdf.addFont('nunitoBoldItalic.ttf', 'Nunito', 'BoldItalic');
  pdf.addFont('nunitoItalic.ttf', 'Nunito', 'Italic');
  pdf.setFont('Nunito', 'Normal');
  pdf.setFontSize(80);

  for await (const slide of slides) {
    await exportSlide(pdf, slide);
  }

  pdf.deletePage(1); // remove default jspdf page

  await pdf.save(fileName, {
    returnPromise: true,
  });
  onSuccess();
};
